import React, { useContext, useMemo, useCallback, Fragment } from 'react';
import { IconButton } from "@material-ui/core";
import { Skeleton } from '@material-ui/lab';
import { EditOutlined as EditIcon, CloseRounded as CancelIcon } from '@material-ui/icons';
import { PatientChartContext } from '../../../contexts/patientChart';
import {
  MODAL_KEY, PATIENT, RESPONSE_POPUP_KEY, CONSULT_TYPE,
  DIMENSION, PRODUCT_INFO, INTAKE,
} from '../../../constants/patientChart.constant';
import PPButton from '../../subComponents/button';
import { POPUP_TYPE } from '../../subComponents/popup';
import Modal from '../../subComponents/commonModal/v3';
import { removeXSSAttacks } from '../../../common/helper';

export const ExtendedConsult = ({
  orderId = 0,
  patientId = 0,
  patient = PATIENT(),
  product = PRODUCT_INFO(),
  saveExtendedConsultInfo = {},
  intake = INTAKE(),
  hasActions = false,

  saveExtendedConsult = async (requestBody = [{
    userId: 0,
    orderId: 0,
    type: '',
    prevValue: '',
    currentValue: '',
    isChanged: false,
  }]) => ({}),
}) => {
  const {
    extendedConsult: {
      current: {
        medications,
        illnesses,
        allergies,
      },
      prev: {
        medications: _medications,
        illnesses: _illnesses,
        allergies: _allergies,
      },
    },
    setExtendedConsult,
    editableField,
    setEditableField,
    handleModalOpen,
    handleMultipleModalClose,
    ModalCollection,
  } = useContext(PatientChartContext);

  const handleConsultChange = (name, { target: { value } }) => {
    setExtendedConsult({
      prev: {
        medications: _medications,
        illnesses: _illnesses,
        allergies: _allergies,
      },
      current: {
        medications,
        illnesses,
        allergies,
        [name]: value,
      },
    });
  }

  const handleSaveExtendedConsult = async ({ key }) => {
    /*
    const { consultId, consultIds } = intake;

    if (consultIds.size > 1) {
      const error = `Multiple consult ids found for this order! Won't be able to save the changes.`;
      const heading = 'Error';

      handleModalOpen({
        key:
        RESPONSE_POPUP_KEY.EXTENDED_CONSULT, modal: {
          heading,
          type: POPUP_TYPE.ERROR,
          message: error,
        }
      });

      return false;
    }
    */

    const requestBody = [
      {
        userId: patientId,
        orderId,
        consultId: 0,
        type: CONSULT_TYPE.medications,
        prevValue: _medications,
        currentValue: medications,
        isChanged: medications !== _medications,
      },  
      {
        userId: patientId,
        orderId,
        consultId: 0,
        type: CONSULT_TYPE.illnesses,
        prevValue: _illnesses,
        currentValue: illnesses,
        isChanged: illnesses !== _illnesses,
      },
      {
        userId: patientId,
        orderId,
        consultId: 0,
        type: CONSULT_TYPE.allergies,
        prevValue: _allergies,
        currentValue: allergies,
        isChanged: allergies !== _allergies,
      }
    ];

    try {
      const { status } = await saveExtendedConsult(requestBody);

      if (status === 200) {
        setExtendedConsult({
          prev: {
            medications: medications,
            illnesses: illnesses,
            allergies: allergies,
          },
          current: {
            medications,
            illnesses,
            allergies,
          },
        });
        const _editableField = new Set(editableField);
        const fields = ['medications', 'allergies', 'illnesses'];
        fields.forEach((field) => {
          _editableField.delete(field);
        });
        setEditableField(_editableField);
        handleMultipleModalClose({ keys: [key] });
      }

      if (status !== 200) {
        const error = 'Failed to save the extended consult. Please try later.';
        const heading = 'Error';

        handleModalOpen({
          key:
          RESPONSE_POPUP_KEY.EXTENDED_CONSULT, modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch(e) {

    }

    return true;
  };

  const handleToggleEditableField = (consultType = '') => {
    const _editableField = new Set(editableField);
    if (editableField.has(consultType)) {
      _editableField.delete(consultType);
    }
    if (!editableField.has(consultType)) {
      _editableField.add(consultType);
    }
    setEditableField(_editableField);
  };

  const isCalled = useMemo(() => {
    return patient.api.called || product.api.called || intake.api.called;
  }, [patient.api, product.api, intake.api]);

  const isLoading = useMemo(() => {
    return patient.api.loading || product.api.loading || intake.api.loading;
  }, [patient.api, product.api, intake.api]);

  const isSaveDisabled = useMemo(() => {
    if (isLoading) {
      return isLoading;
    }

    if (medications !== _medications) {
      return false;
    }

    if (illnesses !== _illnesses) {
      return false;
    }

    if (allergies !== _allergies) {
      return false;
    }

    return true;
  }, [isLoading, medications, _medications, illnesses, _illnesses, allergies, _allergies]);

  const LoadingTemplate = (
    <div className='flex flex-column'>
      <div className='flex m-b-2'>
        <Skeleton variant='text' className='flex min-width-100 width-100' />
      </div>
      <div className='flex m-b-3'>
        <Skeleton variant='text' className='flex flex-fill' />
      </div>

      <div className='flex m-b-2'>
        <Skeleton variant='text' className='flex min-width-90 width-90' />
      </div>
      <div className='flex m-b-3'>
        <Skeleton variant='text' className='flex flex-fill' />
      </div>

      <div className='flex m-b-2'>
        <Skeleton variant='text' className='flex min-width-140 width-140' />
      </div>
      <div className='flex m-b-3'>
        <Skeleton variant='text' className='flex flex-fill' />
      </div>

      <div className='flex m-b-2'>
        <Skeleton variant='text' className='flex min-width-100 width-100' />
      </div>
      <div className='flex m-b-3'>
        <Skeleton variant='text' className='flex flex-fill' />
      </div>
    </div>
  );

  const EditMedicationsButton = new Map([
    [false, (
      <IconButton size='small' className='primary-button' onClick={handleModalOpen.bind(this, { key: MODAL_KEY.EXTENDED_CONSULT })}>
        <EditIcon fontSize='small' />
      </IconButton>
    )],
    [true, (
      <IconButton size='small' className='red-v1-button' onClick={handleToggleEditableField.bind(this, 'medications')}>
        <CancelIcon fontSize='small' />
      </IconButton>
    )],
  ]);

  const EditMedicationsButtonCollection = new Map([
    [true, EditMedicationsButton.get(editableField.has('medications'))],
    [false, <Fragment />],
  ]);

  const EditAllergiesButton = new Map([
    [false, (
      <IconButton size='small' className='primary-button' onClick={handleModalOpen.bind(this, { key: MODAL_KEY.EXTENDED_CONSULT })}>
        <EditIcon fontSize='small' />
      </IconButton>
    )],
    [true, (
      <IconButton size='small' className='red-v1-button' onClick={handleToggleEditableField.bind(this, 'allergies')}>
        <CancelIcon fontSize='small' />
      </IconButton>
    )],
  ]);

  const EditAllergiesButtonCollection = new Map([
    [true, EditAllergiesButton.get(editableField.has('allergies'))],
    [false, <Fragment />],
  ]);

  const EditIllnessesButton = new Map([
    [false, (
      <IconButton size='small' className='primary-button' onClick={handleModalOpen.bind(this, { key: MODAL_KEY.EXTENDED_CONSULT })}>
        <EditIcon fontSize='small' />
      </IconButton>
    )],
    [true, (
      <IconButton size='small' className='red-v1-button' onClick={handleToggleEditableField.bind(this, 'illnesses')}>
        <CancelIcon fontSize='small' />
      </IconButton>
    )],
  ]);

  const EditIllnessesButtonCollection = new Map([
    [true, EditIllnessesButton.get(editableField.has('illnesses'))],
    [false, <Fragment />],
  ]);

  const MedicationsInput = new Map([
    [true, (
      <textarea
        name='medications'
        className='flex flex-fill border-primary-v4-2 min-height-100 p-2 font-proxima-nova font-16 color-dark-grey border-box resize-vertical'
        wrap='soft'
        value={medications}
        onChange={handleConsultChange.bind(this, 'medications')}
      />
    )],
    [false, (
      <div
        className='flex flex-fill border-primary-v4-2 p-2 font-proxima-nova font-16 color-dark-grey border-box min-height-36'
        dangerouslySetInnerHTML={{
          __html: removeXSSAttacks(_medications),
        }}
      />
    )]
  ]);

  const AllergiesInput = new Map([
    [true, (
      <textarea
        name='allergies'
        className='flex flex-fill border-primary-v4-2 min-height-100 p-2 font-proxima-nova font-16 color-dark-grey border-box resize-vertical'
        wrap='soft'
        value={allergies}
        onChange={handleConsultChange.bind(this, 'allergies')}
      />
    )],
    [false, (
      <div
        className='flex flex-fill border-primary-v4-2 p-2 font-proxima-nova font-16 color-dark-grey border-box min-height-36'
        dangerouslySetInnerHTML={{
          __html: removeXSSAttacks(_allergies),
        }}
      />
    )],
  ]);

  const IllnessesInput = new Map([
    [true, (
      <textarea
        name='illnesses'
        className='flex flex-fill border-primary-v4-2 min-height-100 p-2 font-proxima-nova font-16 color-dark-grey border-box resize-vertical'
        wrap='soft'
        // maxLength={65535}
        value={illnesses}
        onChange={handleConsultChange.bind(this, 'illnesses')}
      />
    )],
    [false, (
      <div
        className='flex flex-fill border-primary-v4-2 p-2 font-proxima-nova font-16 color-dark-grey border-box min-height-36'
        dangerouslySetInnerHTML={{
          __html: removeXSSAttacks(_illnesses),
        }}
      />
    )],
  ]);

  const EditModalTemplate = (
    <div className='flex flex-column flex-fill m-b-5'>
      <div className='flex align-items-center justify-content-space-between m-b-1'>
        <div className='flex bold font-16'>List of Medications</div>
      </div>
      <div className='flex align-items-center m-b-5'>
        {MedicationsInput.get(true)}
      </div>

      <div className='flex align-items-center justify-content-space-between m-b-1'>
        <div className='flex bold font-16'>Allergies</div>
      </div>
      <div className='flex align-items-center m-b-5'>
        {AllergiesInput.get(true)}
      </div>

      <div className='flex align-items-center justify-content-space-between m-b-1'>
        <div className='flex bold font-16'>Illnesses</div>
      </div>
      <div className='flex align-items-center m-b-5'>
        {IllnessesInput.get(true)}
      </div>
    </div>
  );

  const EditModalActions = (
    <div className='flex justify-content-flex-end'>
      <PPButton label='Update Extended Consult' variant='contained' loading={saveExtendedConsultInfo.api.loading} disabled={isSaveDisabled} endGutter onClick={handleSaveExtendedConsult.bind(this, { key: MODAL_KEY.EXTENDED_CONSULT, modal: {} })} />
      <PPButton label='Cancel' variant='cancel' disabled={saveExtendedConsultInfo.api.loading} onClick={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.EXTENDED_CONSULT] })} />
    </div>
  );

  const createPopupConfig = useCallback((key = '') => {
    const popup = {
      heading: 'Message',
      type: POPUP_TYPE.INFO,
      message: 'API response appears here...',
    };

    if (!ModalCollection.has(key)) {
      return popup;
    }

    return ModalCollection.get(key) || popup;
  }, [ModalCollection]);

  const EditModal = (
    <Modal
      title={'Edit/Modify Extended Consult'}
      open={ModalCollection.has(MODAL_KEY.EXTENDED_CONSULT)}
      style={DIMENSION.STANDARD}
      responsePopup={ModalCollection.has(RESPONSE_POPUP_KEY.EXTENDED_CONSULT)}
      responsePopupType={createPopupConfig(RESPONSE_POPUP_KEY.EXTENDED_CONSULT).type}
      responsePopupHeading={createPopupConfig(RESPONSE_POPUP_KEY.EXTENDED_CONSULT).heading}
      responsePopupMessage={createPopupConfig(RESPONSE_POPUP_KEY.EXTENDED_CONSULT).message}
      onResponsePopupOk={handleMultipleModalClose.bind(this, { keys: [RESPONSE_POPUP_KEY.EXTENDED_CONSULT] })}
    >
      <div className='flex flex-column flex-fill m-5 patient-chart-container'>
        {EditModalTemplate}
        {EditModalActions}
      </div>
    </Modal>
  )

  const ConsultTemplate = (
    <Fragment>
      <div className='flex align-items-center justify-content-space-between m-b-1'>
        <div className='flex'>List of Medications</div>
        {EditMedicationsButtonCollection.get(hasActions)}
      </div>
      <div className='flex align-items-center m-b-5'>
        {MedicationsInput.get(editableField.has('medications'))}
      </div>

      <div className='flex align-items-center justify-content-space-between m-b-1'>
        <div className='flex'>Allergies</div>
        {EditAllergiesButtonCollection.get(hasActions)}
      </div>
      <div className='flex align-items-center m-b-5'>
        {AllergiesInput.get(editableField.has('allergies'))}
      </div>

      <div className='flex align-items-center justify-content-space-between m-b-1'>
        <div className='flex'>Illnesses</div>
        {EditIllnessesButtonCollection.get(hasActions)}
      </div>
      <div className='flex align-items-center'>
        {IllnessesInput.get(editableField.has('illnesses'))}
      </div>

      {EditModal}
    </Fragment>
  );

  const ConsultTemplateCollection = new Map([
    ['false-false', LoadingTemplate],
    ['false-true', LoadingTemplate],
    ['true-true', LoadingTemplate],
    ['true-false', ConsultTemplate],
  ]);

  return ConsultTemplateCollection.get(`${isCalled}-${isLoading}`);
};

export default ExtendedConsult;
